/**
 * Offline sign-in enrolment (docs/offline/PHASE-8-ARCHITECTURE.md), for a
 * plain browser tab AND the Android kiosk alike: sets (or resets) a local
 * unlock PIN, fetches a signed capability grant this device can verify with
 * no further network call, provisions (or resumes) an OPFS-backed local
 * database, and writes the PIN/grant/permissions into it via
 * `LocalDatabase.auth.enableOfflineSignIn()` - exactly the same orchestration
 * `OfflineAuthRepository` already gives the kiosk build, reused as-is rather
 * than reimplemented here.
 *
 * Requires a live, authenticated session throughout (every step below is an
 * ordinary online API call until the very end) - there is no offline path
 * INTO offline mode, only out of it.
 *
 * A plain browser is normally still in 'api' mode at this point (Phase 8's
 * original design), so this opens a fresh database and closes it again once
 * done - the switch to 'local' mode only takes effect on the next boot,
 * deliberately never hot-swapping the data source under a session already
 * in progress (see posData/index.ts's own file doc). The kiosk is
 * different: it is ALWAYS already in 'local' mode with its own database
 * open (Phase 3), and the OPFS SyncAccessHandle pool VFS underneath only
 * ever allows one open handle per file - a second one would fail outright,
 * not queue - so on the kiosk this reuses that already-open connection via
 * `getActiveLocalDatabase()` instead of opening a second one, and leaves it
 * open afterward since the app is still using it. Either way, enrolling
 * only ever adds an offline-unlock capability to an already-running
 * database; it never changes which database is running.
 *
 * Gated at the call site (Layout.tsx) to a build that actually supports the
 * OPFS/WASM driver (`isOpfsSqliteSupported()`) - this component assumes
 * that is already true and does not re-check it itself.
 */
import { useState, type FormEvent } from 'react';
import { requestPersistentStorage } from '@kortex/pos-local/browser';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { persistedDeviceId } from '../lib/deviceId';
import { getActiveLocalDatabase, openOrProvisionLocalDatabase, setBrowserOfflineModeEnabled } from '../lib/posData';
import { Modal, TextInput, useToast } from '../components/ui';

type Step = 'pin' | 'working' | 'done';

/**
 * `4 to 8 digits` matches the server's own `setOfflinePinSchema`
 * (`@kortex/shared`) - checked here too so a cashier finds out before the
 * request round-trip, not after.
 */
const PIN_PATTERN = /^\d{4,8}$/;

export function OfflineSetupModal({ onClose }: { onClose: () => void }) {
  const { session, push, fail } = useOfflineSetupDeps();
  const [pin, setPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [step, setStep] = useState<Step>('pin');
  const [error, setError] = useState<string | null>(null);
  const [persisted, setPersisted] = useState<boolean | null>(null);

  const mismatch = confirmPin.length > 0 && confirmPin !== pin;
  const validPin = PIN_PATTERN.test(pin);
  const ready = validPin && confirmPin === pin;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!ready || !session) return;
    setStep('working');
    setError(null);
    try {
      const deviceId = persistedDeviceId();

      // 1. Set (or replace) the local unlock PIN - hashed server-side, this
      //    tab only ever stores the returned verifier (see offlineAuth.ts's
      //    own file doc on why the client never hashes it itself).
      const { verifier } = await api.post<{ verifier: string }>('/api/auth/offline-pin', { pin });

      // 2. A signed capability grant this device can verify offline, for up
      //    to the property's configured ceiling.
      const { token, notAfter } = await api.post<{ token: string; notAfter: number }>(
        '/api/auth/offline-grant',
        { deviceId },
      );

      // 3. The pinned public half of that grant's keypair, fetched once
      //    while online (offlineAuth.ts pins it into local_config).
      const { publicKey } = await api.get<{ publicKey: string }>('/api/auth/offline-grant/public-key');

      // 4. The kiosk already has this session's database open (it is
      //    always in 'local' mode) - reuse that exact connection rather
      //    than opening a second one, which the OPFS SyncAccessHandle pool
      //    VFS underneath would simply refuse (one open handle per file,
      //    not one-per-tab-or-caller; see LocalPosDataSource.localDatabase's
      //    file doc). A plain browser still in 'api' mode has nothing open
      //    yet, so it opens (or resumes) one fresh, same as always.
      const alreadyOpen = getActiveLocalDatabase();
      const db = alreadyOpen ?? (await openOrProvisionLocalDatabase(session));
      try {
        await db.auth.enableOfflineSignIn(session.user.id, {
          verifier,
          grantToken: token,
          grantNotAfterEpochSeconds: notAfter,
          grantPublicKeySpki: publicKey,
          // Cached because the grant itself only ever carries a HASH of the
          // permission list (offlineGrant.ts's own design, so a device that
          // is lost or extracted cannot itself reveal the account's full
          // access shape) - see OfflineEnrolmentInput's doc comment.
          permissions: session.permissions,
        });
      } finally {
        if (!alreadyOpen) {
          // OPFS's SyncAccessHandle is exclusive to one open handle at a
          // time (opfs.ts's file doc) - this tab's next boot re-opens the
          // same file, so it must be released here rather than left open.
          // The kiosk's already-open connection (the `alreadyOpen` branch)
          // is left alone - the app is still using it.
          await db.close();
        }
      }

      // Best-effort (requestPersistentStorage() never throws) - the browser
      // may still evict this origin's storage under real disk pressure;
      // there is no stronger guarantee to give beyond asking for one.
      setPersisted(await requestPersistentStorage());

      setBrowserOfflineModeEnabled(true);
      setStep('done');
      push('Offline access is set up on this device', 'ok');
    } catch (caught) {
      setStep('pin');
      const message =
        caught instanceof ApiError
          ? caught.message
          : 'Could not set up offline access. Check your connection and try again.';
      setError(message);
      fail(caught);
    }
  };

  if (step === 'done') {
    return (
      <Modal title="Offline access is ready" onClose={onClose} footer={<button className="btn primary" onClick={onClose}>Done</button>}>
        <div className="col gap-16">
          <p className="small">
            This device can now sign in with your PIN even without a network connection, for as
            long as this property allows offline sign-in. It will take effect the next time you
            open SARTHI here - reload (or come back later) to start using it offline.
          </p>
          {persisted === false ? (
            <p className="small muted">
              This device did not grant persistent storage for offline data. It should still
              work, but under heavy disk pressure it could get cleared sooner than expected.
            </p>
          ) : null}
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      title="Set up offline access"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={step === 'working'}>
            Cancel
          </button>
          <button className="btn primary" type="submit" form="offline-setup-form" disabled={!ready || step === 'working'}>
            {step === 'working' ? 'Setting up…' : 'Turn on offline access'}
          </button>
        </>
      }
    >
      <form id="offline-setup-form" onSubmit={(e) => void submit(e)} className="col gap-16">
        {error ? <div className="form-error">{error}</div> : null}
        <p className="small muted">
          Choose a 4 to 8 digit PIN. If this device loses its internet connection, you can sign
          back in here with this PIN instead - nothing else changes, and every other way you
          sign in stays exactly the same.
        </p>
        <TextInput
          label="PIN"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          autoFocus
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
          hint="4 to 8 digits."
        />
        <TextInput
          label="Confirm PIN"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          value={confirmPin}
          onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, '').slice(0, 8))}
          error={mismatch ? 'Those do not match' : undefined}
        />
        <p className="small muted">
          This downloads a copy of the current menu, tables and in-house guests to this device -
          it can take a few seconds on a slow connection.
        </p>
      </form>
    </Modal>
  );
}

/**
 * `session` is guaranteed non-null everywhere this modal is actually
 * mounted (Layout.tsx only renders it once a session exists) - narrowed
 * here once so the rest of the component isn't sprinkled with `session!`.
 */
function useOfflineSetupDeps() {
  const { session } = useAuth();
  const { push, fail } = useToast();
  return { session, push, fail };
}
